import React from "react";
import { C } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";

/**
 * "Offer on the card + notification" — the OFFER field on the Apple Wallet card.
 *
 * Two things happen with this text, and the copy on the card has to keep them apart:
 *   1. It is printed on the card front (under the stamps) for everyone holding the card.
 *   2. When it CHANGES and the merchant presses "Push update to all wallets", iOS shows the
 *      new text as a notification to every card holder — that is the announcement.
 * Saving alone notifies nobody. Clearing the text removes the field silently.
 */

export const OFFER_TEXT_MAX = 120;
export const OFFER_TEXT_ADVISED = 80;

export const DEFAULT_OFFER_MESSAGE = {
    text: "",
    textAr: "",
    updatedAt: null,
    lastBroadcastAt: null,
    lastBroadcastText: null,
    lastBroadcastTextAr: null,
};

const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box",
};

const Counter = ({ value }) => {
    const n = (value || "").length;
    const tone = n > OFFER_TEXT_MAX ? C.coral : n > OFFER_TEXT_ADVISED ? C.orange : C.textLight;
    return <span style={{ fontSize: 11, color: tone, fontWeight: n > OFFER_TEXT_ADVISED ? 700 : 400 }}>{n} / {OFFER_TEXT_MAX}</span>;
};

const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : null);

/**
 * iOS-style notification mock: the pass icon, the merchant name as title, the text as body.
 * Makes truncation obvious without a device.
 */
const NotificationMock = ({ merchantName, text, dir }) => (
    <div style={{ background: "rgba(120,120,128,0.16)", borderRadius: 16, padding: "10px 12px", display: "flex", gap: 10, alignItems: "flex-start", maxWidth: 360 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }} dir={dir}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{merchantName || "Your brand"}</div>
                <div style={{ fontSize: 10, color: C.textLight, whiteSpace: "nowrap" }}>now</div>
            </div>
            <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {text || <em style={{ color: C.textLight }}>Your offer text appears here</em>}
            </div>
        </div>
    </div>
);

/**
 * @param {object} props
 * @param {object} props.value        offerMessage config (see DEFAULT_OFFER_MESSAGE)
 * @param {(next: object) => void} props.onChange
 * @param {string} props.merchantName
 * @param {object|null} props.summary  from GET /api/loyalty/wallet-passes/refresh-all/summary
 */
const OfferMessageCard = ({ value, onChange, merchantName, summary }) => {
    const offer = { ...DEFAULT_OFFER_MESSAGE, ...(value || {}) };
    const set = (key, v) => onChange({ ...offer, [key]: v });

    const broadcast = summary?.broadcast;
    const pendingAnnouncement = broadcast?.willNotify;

    return (
        <RCard title="📣 Offer on the card + notification">
            <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.55, marginBottom: 14 }}>
                Printed on the card front under the stamps. When you <b>change</b> it and then press
                <b> Push update to all wallets</b>, every customer holding the card gets it as a notification
                (title: your brand, body: this text). Keep it under ~{OFFER_TEXT_ADVISED} characters and say when it
                ends — a phone that was offline sees it late. Saving on its own notifies nobody; clearing the
                text removes the field silently.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Offer text (English)</div>
                        <Counter value={offer.text} />
                    </div>
                    <input
                        type="text"
                        maxLength={OFFER_TEXT_MAX}
                        value={offer.text || ""}
                        onChange={(e) => set("text", e.target.value)}
                        placeholder="e.g. Free espresso with any pastry — this weekend only"
                        style={inputStyle}
                    />
                </div>
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Offer text (Arabic)</div>
                        <Counter value={offer.textAr} />
                    </div>
                    <input
                        type="text"
                        dir="rtl"
                        maxLength={OFFER_TEXT_MAX}
                        value={offer.textAr || ""}
                        onChange={(e) => set("textAr", e.target.value)}
                        placeholder="اختياري — يظهر لعملاء اللغة العربية"
                        style={inputStyle}
                    />
                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 5 }}>Shown to customers whose app language is Arabic. Leave empty to reuse the English text.</div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>How it will look</div>
                    <NotificationMock merchantName={merchantName} text={offer.text} />
                </div>
                {offer.textAr ? (
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Arabic</div>
                        <NotificationMock merchantName={merchantName} text={offer.textAr} dir="rtl" />
                    </div>
                ) : null}
            </div>

            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.borderLight}`, display: "flex", flexWrap: "wrap", gap: "6px 20px", fontSize: 12, color: C.textMed }}>
                <span>Last edited: <b style={{ color: C.text }}>{formatWhen(offer.updatedAt) || "—"}</b></span>
                <span>
                    Last announced: <b style={{ color: C.text }}>{formatWhen(offer.lastBroadcastAt) || "never"}</b>
                    {offer.lastBroadcastText ? <span style={{ color: C.textLight }}> · “{offer.lastBroadcastText}”</span> : null}
                </span>
                {pendingAnnouncement ? (
                    <span style={{ color: C.orange, fontWeight: 700 }}>● Saved text not yet announced — push to all wallets to send it</span>
                ) : null}
            </div>
        </RCard>
    );
};

export default OfferMessageCard;
